// License administration. Trial / Commercial / Internal envelopes
// are pasted here; the backend Ed25519-verifies, persists, and the
// feature gates flip live.
//
// Procurement flow:
//   1. Admin lands on this page, sees the Install-ID and "trial"
//      state with N days remaining.
//   2. Sends Install-ID to procurement, gets back a signed
//      envelope (~1 KB blob).
//   3. Pastes it into the textarea, clicks Apply.
//   4. Page refreshes — state flips to 'licensed_active', features
//      light up green.

import { useState } from 'react';
import {
  Award, Copy, Check, Trash2, AlertTriangle, ShieldCheck, Info,
  Upload, KeyRound, FolderPlus, Users, ShieldQuestion, Mail,
  Repeat, RefreshCcw, Webhook, Calendar, Lock, Clock, X, ExternalLink,
  Shield, ScanSearch, ShieldAlert, FileCheck, ScrollText, Plane,
  Database, Save, Palette, BookOpen, FileBox, Bot, Bell,
  Boxes, GitBranch, Cloud, FileArchive,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardFooter, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import {
  useLicense,
  useLicenseEvents,
  useLicenseFeatures,
  applyLicense,
  removeLicense,
  type FeatureCategory,
  type FeatureRow,
  type FeatureMatrix,
  type LicenseEvent,
  type LicenseState,
  type LicenseKind,
} from '../../api/license';

function stateTone(s: LicenseState): 'success' | 'warning' | 'danger' | 'neutral' | 'accent' {
  switch (s) {
    case 'licensed_active': return 'success';
    case 'trial_active':    return 'accent';
    case 'trial_expired':   return 'warning';
    case 'licensed_expired':return 'danger';
    case 'invalid':         return 'danger';
  }
}

function stateLabel(s: LicenseState, k: LicenseKind): string {
  switch (s) {
    case 'licensed_active':  return k === 'internal' ? 'Internal license' : 'Licensed';
    case 'trial_active':     return 'Trial';
    case 'trial_expired':    return 'Trial expired';
    case 'licensed_expired': return 'License expired';
    case 'invalid':          return 'Invalid';
  }
}

// Icon mapping by feature ID. Labels / descriptions / categories come
// from the backend feature catalogue (/api/v1/license/features) so a
// future backend-side feature lights up automatically; we only have to
// map an icon here, with ShieldQuestion as the fallback.
const FEATURE_ICONS: Record<string, typeof Upload> = {
  // Core
  'write.api':                  Upload,
  'repo.create':                FolderPlus,
  'token.issue':                KeyRound,
  'service.account':            Bot,
  'auth.local':                 KeyRound,
  'user.invite':                Users,
  'build.info':                 FileBox,
  // Security
  'security.verify_on_pull':    ShieldCheck,
  'security.detection_scan':    ScanSearch,
  'security.block':             ShieldAlert,
  'security.auto_quarantine':   Shield,
  'security.pull_gate':         Shield,
  'security.license_compliance':FileCheck,
  'audit.log':                  ScrollText,
  'air_gapped':                 Plane,
  // Operations
  'retention':                  Database,
  'backup':                     Save,
  'branding':                   Palette,
  'doc.site':                   BookOpen,
  // Auth
  'sso.saml':                   Lock,
  'oidc.exchange':              GitBranch,
  // Replication
  'repl.push':                  Repeat,
  'repl.pull':                  RefreshCcw,
  // Automation
  'webhooks':                   Webhook,
  'notify.channels':            Bell,
  'scheduler':                  Calendar,
  // Migration importers
  'import.jfrog':               Boxes,
  'import.nexus':               Boxes,
  'import.github':              GitBranch,
  'import.gitlab':              GitBranch,
  'import.azure':               Cloud,
  'import.aws':                 Cloud,
  'import.google':              Cloud,
  'import.bulk':                FileArchive,
};

const CATEGORY_LABELS: Record<FeatureCategory, string> = {
  core:        'Core hosting',
  security:    'Security',
  operations:  'Operations',
  auth:        'Authentication',
  replication: 'Replication',
  automation:  'Automation',
  migration:   'Migration importers',
};

// Free-tier categories (Security Promise + Core Hosting) render first;
// Commercial-tier categories follow.
const FREE_CATEGORY_ORDER: FeatureCategory[] = ['security', 'core', 'operations'];
const COMMERCIAL_CATEGORY_ORDER: FeatureCategory[] = ['auth', 'replication', 'automation', 'migration'];

function iconFor(key: string): typeof Upload {
  return FEATURE_ICONS[key] ?? ShieldQuestion;
}

function featureTileClassName(on: boolean, trialLocking: boolean): string {
  if (!on) return 'border-danger/30 bg-danger/5';
  return trialLocking ? 'border-warning/30 bg-warning/5' : 'border-success/30 bg-success/5';
}

function featureIconClassName(on: boolean, trialLocking: boolean): string {
  if (!on) return 'text-danger';
  return trialLocking ? 'text-warning' : 'text-success';
}

function daysRemainingClassName(days: number): string {
  if (days <= 7) return 'text-danger text-2xl font-display mt-1';
  if (days <= 30) return 'text-warning text-2xl font-display mt-1';
  return 'text-fg text-2xl font-display mt-1';
}

function featureMatrixSubtitle(state: LicenseState, days: number, commercialCount: number): string {
  switch (state) {
    case 'trial_active':
      return `${days} day${days === 1 ? '' : 's'} left in trial — ${commercialCount} commercial features will lock at expiry. Free-tier (Security + Core Hosting) keeps running indefinitely.`;
    case 'licensed_active':
      return `Commercial license — all features active, no expiration. Security Promise is always in effect.`;
    case 'trial_expired':
      return `Trial expired — ${commercialCount} commercial features locked. Apply a commercial envelope below to restore them. Free-tier (Security + Core Hosting) is unaffected.`;
    case 'licensed_expired':
      return `License expired — ${commercialCount} commercial features locked. Renew the envelope to restore them. Free-tier (Security + Core Hosting) is unaffected.`;
    case 'invalid':
      return `License invalid — ${commercialCount} commercial features locked. Free-tier (Security + Core Hosting) is unaffected.`;
  }
}

function SecurityPromiseCard() {
  const items = [
    'Verify-on-pull + trust bundles',
    'CVE detection + signature checks',
    'Security blocks + auto-quarantine',
    'Pull-gate policy engine',
    'License compliance',
    'Audit log',
    'Air-gapped mode',
  ];
  return (
    <div
      className="rounded-md border-2 border-success/40 bg-success/5 p-5 shadow-[0_0_24px_rgba(34,197,94,0.08)]"
      role="region"
      aria-label="Security Promise"
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="h-7 w-7 text-success shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-base font-display text-fg">
            Our security promise
          </p>
          <p className="text-sm text-fg-muted mt-1">
            Security is not a paywall. The features below are{' '}
            <span className="font-medium text-success">free forever</span>,
            even after your trial expires:
          </p>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {items.map((label) => (
              <li key={label} className="flex items-center gap-2 text-fg">
                <Check className="h-3.5 w-3.5 text-success shrink-0" />
                {label}
              </li>
            ))}
          </ul>
          <p className="text-sm text-fg-muted mt-3">
            Plus core hosting — artifact upload, API tokens, service accounts,
            local user login — stays available indefinitely. Pay only when you
            need premium integrations (webhooks, SSO, replication, migration).
          </p>
        </div>
      </div>
    </div>
  );
}

function FeatureStatusIndicator({
  on,
  trialLocking,
  isFreeTier,
  commercialUnlimited,
  trialLocksInDays,
}: Readonly<{
  on: boolean;
  trialLocking: boolean;
  isFreeTier: boolean;
  commercialUnlimited: boolean;
  trialLocksInDays?: number;
}>) {
  if (!on) return <Badge tone="danger">locked</Badge>;
  if (trialLocking) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-warning shrink-0 whitespace-nowrap">
        <Clock className="h-3 w-3" />
        locks in {trialLocksInDays}d
      </span>
    );
  }
  if (isFreeTier) {
    return (
      <Badge tone="success">
        <ShieldCheck className="h-3 w-3" />
        free forever
      </Badge>
    );
  }
  if (commercialUnlimited) {
    return (
      <Badge tone="success">
        <Check className="h-3 w-3" />
        active
      </Badge>
    );
  }
  return <Check className="h-3.5 w-3.5 text-success shrink-0" />;
}

function FeatureTile({
  feature,
  state,
}: Readonly<{
  feature: FeatureRow;
  state: LicenseState;
}>) {
  const Icon = iconFor(feature.id);
  const on = feature.currently_unlocked;
  const trialLocking = on && state === 'trial_active' && (feature.trial_locks_in_days ?? 0) >= 0
    && !feature.unlocked_in_states.includes('trial_expired');
  const commercialUnlimited = on && state === 'licensed_active';
  const isFreeTier = feature.tier === 'free';

  return (
    <div className={'rounded-md border p-3 ' + featureTileClassName(on, trialLocking)}>
      <div className="flex items-start gap-2">
        <Icon className={'h-4 w-4 mt-0.5 shrink-0 ' + featureIconClassName(on, trialLocking)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className={'text-sm font-medium ' + (on ? 'text-fg' : 'text-fg-muted')}>
              {feature.label}
            </p>
            <FeatureStatusIndicator
              on={on}
              trialLocking={trialLocking}
              isFreeTier={isFreeTier}
              commercialUnlimited={commercialUnlimited}
              trialLocksInDays={feature.trial_locks_in_days}
            />
          </div>
          {feature.description && (
            <p className="text-xs text-fg-muted mt-1 leading-snug">
              {feature.description}
            </p>
          )}
          {feature.real_world_impact && (
            <p
              className={
                'text-[11px] mt-1.5 leading-snug ' +
                (on ? 'text-fg-subtle italic' : 'text-danger/90')
              }
            >
              {on
                ? `After lock: ${feature.real_world_impact}`
                : feature.real_world_impact}
            </p>
          )}
          <p className="text-[10px] text-fg-subtle font-mono mt-1.5">{feature.id}</p>
        </div>
      </div>
    </div>
  );
}

function ProcurementModal({
  installID,
  expiresAt,
  procurement,
  onClose,
}: Readonly<{
  installID: string;
  expiresAt?: string;
  procurement: { email?: string; url?: string };
  onClose: () => void;
}>) {
  const expiresFmt = expiresAt
    ? new Date(expiresAt).toISOString().slice(0, 10)
    : '(unknown)';
  const subject = 'OrbitalReg license envelope request';
  const body = `Subject: ${subject}
Install-ID: ${installID}
Platform: orbitalreg
Trial expires: ${expiresFmt}
Customer: <yourcompany>

Please issue a commercial license envelope bound to the Install-ID above.
Reply with the signed envelope and our admin will paste it into the
License page to activate the platform.`;
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    await navigator.clipboard.writeText(body);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const mailto = procurement.email
    ? `mailto:${procurement.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    : null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-xl bg-bg-elevated border border-border rounded-md shadow-xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <p className="text-sm font-medium text-fg">Send Install-ID to procurement</p>
            <p className="text-xs text-fg-muted">
              Copy the block below into a new email to your procurement team — they reply with a
              signed envelope you paste into the License page.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-bg-hover text-fg-muted"
            aria-label="close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <pre className="whitespace-pre-wrap break-words rounded-md bg-bg-inset border border-border p-3 font-mono text-xs text-fg">
            {body}
          </pre>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" onClick={onCopy}>
              {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy text block'}
            </Button>
            {mailto && (
              <a
                href={mailto}
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-border hover:bg-bg-hover text-sm text-fg"
              >
                <Mail className="h-4 w-4" />
                Open in mail client{' '}
                <span className="text-fg-subtle">({procurement.email})</span>
              </a>
            )}
            {procurement.url && (
              <a
                href={procurement.url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-border hover:bg-bg-hover text-sm text-fg"
              >
                <ExternalLink className="h-4 w-4" />
                License request portal
              </a>
            )}
          </div>
          {!procurement.email && !procurement.url && (
            <p className="text-xs text-fg-muted">
              Tip: set <span className="orb-code">ORBITALREG_PROCUREMENT_EMAIL</span> or{' '}
              <span className="orb-code">ORBITALREG_LICENSE_REQUEST_URL</span> on the server to
              add a one-click contact link here.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function FeatureCategorySection({
  categories,
  features,
  tier,
  state,
}: Readonly<{
  categories: readonly FeatureCategory[];
  features: FeatureRow[];
  tier: 'free' | 'commercial';
  state: LicenseState;
}>) {
  return (
    <div className="space-y-4">
      {categories.map((cat) => {
        const inCat = features.filter((f) => f.tier === tier && f.category === cat);
        if (inCat.length === 0) return null;
        return (
          <div key={cat}>
            <p className="text-xs font-medium text-fg-muted mb-2">
              {CATEGORY_LABELS[cat]}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {inCat.map((f) => (
                <FeatureTile key={f.id} feature={f} state={state} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FeatureMatrixCard({ matrix }: Readonly<{ matrix: FeatureMatrix }>) {
  return (
    <Card>
      <CardHeader
        title="What this license unlocks"
        description={featureMatrixSubtitle(
          matrix.current_state,
          matrix.days_remaining,
          matrix.features.filter((f) => f.tier === 'commercial').length,
        )}
      />
      <CardBody className="space-y-6">
        <section>
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-4 w-4 text-success" />
            <p className="orb-section-title text-success">
              Free forever — Security Promise
            </p>
          </div>
          <FeatureCategorySection
            categories={FREE_CATEGORY_ORDER}
            features={matrix.features}
            tier="free"
            state={matrix.current_state}
          />
        </section>

        <section>
          <div className="flex items-center gap-2 mb-2">
            <Award className="h-4 w-4 text-accent" />
            <p className="orb-section-title text-accent">
              Commercial — Premium integrations
              {matrix.current_state === 'trial_active' && matrix.days_remaining > 0 && (
                <span className="ml-2 text-fg-muted normal-case font-normal">
                  (locks after trial in {matrix.days_remaining} day
                  {matrix.days_remaining === 1 ? '' : 's'})
                </span>
              )}
            </p>
          </div>
          <FeatureCategorySection
            categories={COMMERCIAL_CATEGORY_ORDER}
            features={matrix.features}
            tier="commercial"
            state={matrix.current_state}
          />
        </section>

        <div className="rounded-md bg-bg-inset/40 border border-border p-3 flex items-start gap-2 text-xs text-fg-muted">
          <Info className="h-4 w-4 text-fg-muted mt-0.5 shrink-0" />
          <p>
            Note: artifact pulls are never gated by license state — read paths stay open
            even after expiry. See <span className="orb-code">LICENSING-ROADMAP.md §5</span>
            {' '}for the full degraded-mode contract, or{' '}
            <span className="orb-code">docs-site/compliance/license-tiers.md</span> for the
            Free vs Commercial tier breakdown.
          </p>
        </div>
      </CardBody>
    </Card>
  );
}

function LicenseEventDetail({ detail }: Readonly<{ detail: unknown }>) {
  if (!detail) return <>—</>;
  return (
    <details>
      <summary className="cursor-pointer hover:text-fg">view</summary>
      <pre className="mt-1 p-2 rounded bg-bg-inset text-[10px] max-w-md overflow-auto">
        {JSON.stringify(detail, null, 2)}
      </pre>
    </details>
  );
}

function LicenseEventsCard({ events }: Readonly<{ events: LicenseEvent[] | undefined }>) {
  return (
    <Card>
      <CardHeader title="Recent license events" description="Append-only audit of applies, removes, and validation failures." />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="px-5 py-3 font-medium text-fg-muted">When</th>
              <th className="px-5 py-3 font-medium text-fg-muted">Action</th>
              <th className="px-5 py-3 font-medium text-fg-muted">Actor</th>
              <th className="px-5 py-3 font-medium text-fg-muted">License ID</th>
              <th className="px-5 py-3 font-medium text-fg-muted">Detail</th>
            </tr>
          </thead>
          <tbody>
            {events?.length ? (
              events.map((e) => (
                <tr key={`${e.at}-${e.kind}-${e.actor ?? ''}`} className="border-b border-border/60 last:border-0">
                  <td className="px-5 py-2 text-fg-muted text-xs whitespace-nowrap">
                    {new Date(e.at).toLocaleString()}
                  </td>
                  <td className="px-5 py-2">
                    <span className="orb-code text-xs">{e.kind}</span>
                  </td>
                  <td className="px-5 py-2 text-fg-muted text-xs">{e.actor || '—'}</td>
                  <td className="px-5 py-2 text-fg-muted font-mono text-xs">{e.license_id || '—'}</td>
                  <td className="px-5 py-2 text-fg-muted text-xs">
                    <LicenseEventDetail detail={e.detail} />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-fg-muted text-sm">
                  No events yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function TrialExpiryBanner({
  daysRemaining,
  onSendToProcurement,
}: Readonly<{ daysRemaining: number; onSendToProcurement: () => void }>) {
  return (
    <div
      className="rounded-md border border-warning/40 bg-warning/5 p-4 flex items-start gap-3"
      role="status"
    >
      <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">
        <p className="text-fg font-medium">
          Trial expires in {daysRemaining} day{daysRemaining === 1 ? '' : 's'}.
        </p>
        <p className="text-fg-muted mt-1">
          Webhooks, SSO, replication, and migration importers will stop
          working — but your <span className="text-success font-medium">artifacts,
          security gates, and audit log will continue uninterrupted</span>. Apply
          a commercial license envelope to keep premium integrations active.
        </p>
      </div>
      <Button
        variant="primary"
        size="sm"
        onClick={onSendToProcurement}
      >
        <Mail className="h-4 w-4" />
        Send Install-ID to procurement →
      </Button>
    </div>
  );
}

export function AdminLicense() {
  const { data: lic, mutate, isLoading } = useLicense();
  const { data: evs } = useLicenseEvents(20);
  const { data: matrix } = useLicenseFeatures();
  const [envelope, setEnvelope] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showProcurement, setShowProcurement] = useState(false);

  const onApply = async () => {
    if (!envelope.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await applyLicense(envelope.trim());
      setEnvelope('');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    if (
      !confirm(
        'Remove the active license? The platform falls back to trial state (or trial-expired if the original trial window is over). Write APIs may stop working.',
      )
    ) {
      return;
    }
    try {
      await removeLicense();
    } catch (e) {
      alert(String(e));
    }
    await mutate();
  };

  const onCopyInstallID = async () => {
    if (!lic) return;
    await navigator.clipboard.writeText(lic.install_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AdminShell
      title="License"
      description="OrbitalReg ships under a two-tier model: Free Forever (Security + Core Hosting — uploads, tokens, CVE detection, audit, pull-gate) is always available, and Commercial (premium integrations — webhooks, SSO, replication, migration importers) requires an active trial or commercial envelope."
    >
      {isLoading && (
        <Card><CardBody><p className="text-fg-muted">Loading…</p></CardBody></Card>
      )}

      {lic && (
        <>
          {lic.state === 'trial_active' && lic.days_remaining > 0 && lic.days_remaining < 7 && (
            <TrialExpiryBanner
              daysRemaining={lic.days_remaining}
              onSendToProcurement={() => setShowProcurement(true)}
            />
          )}

          <SecurityPromiseCard />

          <Card>
            <CardHeader
              title="Current state"
              actions={
                <Badge tone={stateTone(lic.state)}>
                  <Award className="h-3 w-3" />
                  {stateLabel(lic.state, lic.kind)}
                </Badge>
              }
            />
            <CardBody className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="orb-section-title text-fg-muted">Customer</p>
                <p className="text-fg mt-1">
                  {lic.customer || <span className="text-fg-subtle italic">— (trial)</span>}
                </p>
              </div>
              <div>
                <p className="orb-section-title text-fg-muted">License ID</p>
                <p className="text-fg mt-1 font-mono text-xs break-all">
                  {lic.license_id || <span className="text-fg-subtle italic">none</span>}
                </p>
              </div>
              <div>
                <p className="orb-section-title text-fg-muted">Days remaining</p>
                <p className={daysRemainingClassName(lic.days_remaining)}>
                  {Math.max(lic.days_remaining, 0)}
                </p>
              </div>
              {lic.expires_at && (
                <div>
                  <p className="orb-section-title text-fg-muted">Expires</p>
                  <p className="text-fg mt-1">{new Date(lic.expires_at).toLocaleString()}</p>
                </div>
              )}
              {lic.first_boot && (
                <div>
                  <p className="orb-section-title text-fg-muted">First boot</p>
                  <p className="text-fg mt-1">{new Date(lic.first_boot).toLocaleDateString()}</p>
                </div>
              )}
              {lic.invalid_reason && (
                <div className="md:col-span-3">
                  <p className="orb-section-title text-danger/80">Invalid</p>
                  <p className="text-danger font-mono text-xs mt-1">{lic.invalid_reason}</p>
                </div>
              )}
            </CardBody>
            {lic.state !== 'trial_active' && lic.kind !== 'trial' && (
              <CardFooter className="flex justify-end">
                <Button variant="secondary" onClick={onRemove}>
                  <Trash2 className="h-4 w-4" />
                  Remove license
                </Button>
              </CardFooter>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Install ID"
              description="Send this opaque ID to procurement when ordering a license. The signed envelope you receive back will only verify against this ID — they're bound 1:1."
            />
            <CardBody>
              <div className="flex items-center gap-2 p-3 rounded-md bg-bg-inset border border-border font-mono text-sm break-all">
                <ShieldCheck className="h-4 w-4 text-accent shrink-0" />
                <span className="flex-1">{lic.install_id}</span>
                <button
                  type="button"
                  onClick={onCopyInstallID}
                  className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-border hover:bg-bg-hover text-fg-muted hover:text-fg text-sm shrink-0"
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Apply license envelope"
              description={
                lic.state === 'licensed_active'
                  ? 'Already licensed. Pasting a new envelope replaces the active one (e.g. to extend the term).'
                  : 'Paste the Ed25519-signed envelope you received from procurement. Format is one line of base64 (or armoured multi-line; both work).'
              }
            />
            <CardBody>
              <textarea
                value={envelope}
                onChange={(e) => setEnvelope(e.target.value)}
                placeholder="-----BEGIN ORBITALREG LICENSE-----&#10;…signed envelope here…&#10;-----END ORBITALREG LICENSE-----"
                rows={8}
                className="w-full px-3 py-2 rounded-md bg-bg-inset text-fg placeholder:text-fg-subtle border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-xs font-mono"
                spellCheck={false}
              />
              {err && (
                <div className="mt-3 rounded-md bg-danger/5 border border-danger/40 p-3 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-danger mt-0.5 shrink-0" />
                  <p className="text-danger font-mono text-xs">{err}</p>
                </div>
              )}
              <div className="mt-3 rounded-md bg-bg-inset/40 border border-border p-3 flex items-start gap-2 text-xs text-fg-muted">
                <Info className="h-4 w-4 text-fg-muted mt-0.5 shrink-0" />
                <p>
                  The envelope is verified against the OrbitalReg root public key bundled with this build,
                  and against your install ID above. A signature mismatch or install-ID mismatch is rejected
                  with a clear error.
                </p>
              </div>
            </CardBody>
            <CardFooter className="flex justify-end">
              <Button
                variant="primary"
                disabled={busy || !envelope.trim()}
                onClick={onApply}
              >
                <Award className="h-4 w-4" />
                Apply license
              </Button>
            </CardFooter>
          </Card>

          {matrix && <FeatureMatrixCard matrix={matrix} />}

          <LicenseEventsCard events={evs?.events} />
        </>
      )}
      {showProcurement && lic && (
        <ProcurementModal
          installID={lic.install_id}
          expiresAt={lic.expires_at}
          procurement={matrix?.procurement ?? {}}
          onClose={() => setShowProcurement(false)}
        />
      )}
    </AdminShell>
  );
}
